import {
  Environment,
  Network,
  RecordSource,
  Store,
  RequestParameters,
  Variables,
  CacheConfig,
  UploadableMap,
} from "relay-runtime";
import axios, { isAxiosError } from "axios";
import type {
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import _ from "lodash";
import { apiEndpoint, appVersion } from "@constants/Env";
import { setExceptionalError, errorAtom } from "@hooks/useExecptionalError";
import refreshToken from "@lib/util/refreshToken";
import { SecureStoreManager } from "@lib/util/secureStoreManager";
import FormData from "form-data";
import { Platform } from "react-native";
import { resolveError } from "@lib/util/error";
import ExceptionError from "@lib/errors/exceptionError";
import { sendError, notify } from "@lib/util/logger";
import * as Crypto from "expo-crypto";
import { tryIOSFormDataUploadFallback } from "@lib/util/iosFormDataUpload";
import { tryWebFormDataUploadFallback } from "@lib/util/webFormDataUpload";

// multipartアップロードリクエストを識別するためのAxios config拡張
declare module "axios" {
  interface AxiosRequestConfig {
    _isMultipartUpload?: boolean;
  }
}

type ErrorCodeType =
  | "Maintenance"
  | "NetworkError"
  | "ServiceDown"
  | "Upgrade"
  | "Expired"
  | "Unknown";

type RequestInfo = {
  url: string;
  method: string;
  headers: Record<string, string>;
  params?: Record<string, unknown>;
  query?: string;
  variables?: Variables;
  timestamp: string;
  appVersion: string;
  data?: unknown;
  timeout?: number;
  baseURL?: string;
  operationName?: string; // GraphQL Operation名（例: PostDraftScreenQuery）
  queryPreview?: string; // クエリの最初の200文字
};

type DetailedErrorType = {
  code: ErrorCodeType;
  message: string;
  request?: RequestInfo;
  response?: AxiosResponse<unknown, unknown>;
  timestamp?: string;
  stack?: string;
  name?: string;
  severity?: "fatal" | "error" | "warning" | "info" | "debug";
  additionalInfo?: {
    networkInfo?: {
      online?: boolean;
    };
    environment?: {
      userAgent?: string;
      platform?: string;
      language?: string;
    };
  };
  details?: {
    statusCode?: number;
    statusText?: string;
    originalError?: string;
    timestamp?: string;
  };
  error: unknown;
};

type ResponseError = {
  message: string;
  locations: {
    [index: string]: unknown;
  }[];
  path: unknown[];
  extensions: {
    code: string;
  };
};

type ApiResponse<T = object> = {
  errors?: ResponseError[];
  data: T;
};

type Maintenance = {
  code: string;
  message: string;
};

const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

/**
 * GraphQLリクエストからOperation名や詳細情報を抽出するヘルパー関数
 * ERR_NETWORKエラー発生時にどのGraphQLクエリが失敗したかを特定するために使用
 */
const extractGraphQLInfo = (
  data: unknown
): {
  query?: string;
  variables?: Variables;
  operationName?: string;
  queryPreview?: string;
} => {
  try {
    let parsedData: { query?: string; variables?: Variables } | undefined;

    // dataが文字列の場合はJSON.parseする
    if (typeof data === "string") {
      parsedData = JSON.parse(data) as {
        query?: string;
        variables?: Variables;
      };
    } else if (typeof data === "object" && data !== null) {
      parsedData = data as { query?: string; variables?: Variables };
    }

    if (parsedData?.query != null && parsedData.query !== "") {
      // Operation名を抽出（query OperationName または mutation OperationName）
      const operationMatch = parsedData.query.match(
        /(?:query|mutation)\s+(\w+)/
      );
      const operationName = operationMatch?.[1] ?? "Unknown";

      // クエリのプレビュー（最初の200文字）
      const queryPreview = parsedData.query.substring(0, 200);

      return {
        query: parsedData.query,
        variables: parsedData.variables,
        operationName,
        queryPreview,
      };
    }
  } catch {
    // パースエラーは無視
  }
  return {};
};

const collectRequestInfo = (
  config: InternalAxiosRequestConfig,
  query?: string,
  variables?: Variables
): RequestInfo => {
  // GraphQL情報を抽出（query/variablesが渡されていない場合）
  const graphqlInfo =
    query == null || query === "" ? extractGraphQLInfo(config.data) : {};

  return {
    url: config.url ?? apiEndpoint,
    method: (config.method?.toUpperCase() ?? "POST") as string,
    headers: _.omit(config.headers as Record<string, string>, [
      "Authorization",
    ]),
    params: config.params as Record<string, unknown>,
    query: query ?? graphqlInfo.query,
    variables: variables ?? graphqlInfo.variables,
    timestamp: new Date().toISOString(),
    appVersion: appVersion ?? "unknown",
    data: config.data,
    timeout: config.timeout,
    baseURL: config.baseURL,
    // GraphQL専用フィールドを条件付きで追加
    ...(graphqlInfo.operationName != null && graphqlInfo.operationName !== ""
      ? {
          operationName: graphqlInfo.operationName,
          queryPreview: graphqlInfo.queryPreview,
        }
      : {}),
  };
};

const handleSetError = async (error: DetailedErrorType): Promise<void> => {
  try {
    const networkInfo = {
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    };

    const enhancedError = {
      ...error,
      timestamp: new Date().toISOString(),
      name: error.code,
      additionalInfo: {
        networkInfo,
        environment: {
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          platform:
            typeof navigator !== "undefined" ? navigator.platform : undefined,
          language:
            typeof navigator !== "undefined" ? navigator.language : undefined,
        },
      },
    };

    setExceptionalError(errorAtom, {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    // ExceptionErrorを作成してsendErrorを使用
    const exceptionError = new ExceptionError(error.message, {
      name: error.code,
      cause: error.error,
    });

    await sendError(exceptionError, {
      extra: {
        code: error.code,
        request: error.request,
        response: error.response,
        additionalInfo: enhancedError.additionalInfo,
        timestamp: enhancedError.timestamp,
        severity:
          error.severity ?? (error.code === "Unknown" ? "error" : "warning"),
      },
    });

    // Slack通知（より詳細な情報を含める）
    const slackNotification = {
      type: "RelayEnvironmentError",
      errorInfo: {
        code: error.code,
        message: error.message,
        severity: error.severity ?? "error",
        details: error.details,
      },
      request:
        error.request != null
          ? {
              url: error.request.url,
              method: error.request.method,
              headers: error.request.headers,
              query: error.request.query,
              variables: error.request.variables,
              params: error.request.params,
              data: error.request.data,
              timeout: error.request.timeout,
              baseURL: error.request.baseURL,
              timestamp: error.request.timestamp,
              appVersion: error.request.appVersion,
            }
          : undefined,
      response:
        error.response != null
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              headers: error.response.headers,
              data:
                typeof error.response.data === "object"
                  ? JSON.stringify(error.response.data, null, 2).substring(
                      0,
                      5000
                    ) // 最大5000文字に制限
                  : error.response.data,
              config: {
                url: error.response.config?.url,
                method: error.response.config?.method,
              },
            }
          : undefined,
      networkInfo: enhancedError.additionalInfo?.networkInfo,
      environment: enhancedError.additionalInfo?.environment,
      timestamp: enhancedError.timestamp,
      errorStack: error.error instanceof Error ? error.error.stack : undefined,
      originalError:
        error.error instanceof Error
          ? {
              name: error.error.name,
              message: error.error.message,
              cause: error.error.cause,
            }
          : error.error,
    };

    await notify(slackNotification, { level: "error" });
  } catch (e) {
    // エラーハンドリング自体のエラーを処理
    // eslint-disable-next-line no-console
    console.error("Error in handleSetError:", e);

    const handlerError = new ExceptionError("Error handling failed", {
      name: "ErrorHandlerFailure",
      cause: e,
    });

    await sendError(handlerError, {
      extra: {
        originalError: {
          code: error.code,
          message: error.message,
        },
      },
    });
  }
};

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const newConfig = { ...config };
    newConfig.headers.set("AppVersion", appVersion);
    const token = await SecureStoreManager.getAccessToken();
    if (token !== null) {
      newConfig.headers.set("Authorization", `Bearer ${token}`);
    }
    return newConfig;
  }
);

axiosInstance.interceptors.response.use(
  async (response: AxiosResponse<ApiResponse>) => {
    if (response.data.errors != null) {
      const error = _.head(response.data.errors);
      if (error?.extensions?.code === "TokenExpired") {
        return retryWithtokenRefresh(response);
      }
      await handleError(
        error,
        response.config as InternalAxiosRequestConfig,
        response
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 503) {
      const { data } = error.response as AxiosResponse<Maintenance>;
      if (data.code === "UnderMaintenance") {
        await handleSetError({
          code: "Maintenance",
          message: data.message,
          request:
            error.config != null
              ? collectRequestInfo(error.config as InternalAxiosRequestConfig)
              : undefined,
          response: error.response,
          error,
          severity: "warning",
        });
        return;
      }
    }
    const result = resolveError(error);

    // エラーコードをより詳細に判定
    let errorCode: ErrorCodeType = "ServiceDown";
    let networkDiagnostics: Record<string, unknown> = {};

    // Network Errorの場合、追加診断情報を収集
    if (result.code === "ERR_NETWORK" || error.message === "Network Error") {
      errorCode = "NetworkError";

      // 追加診断情報を安全に収集
      try {
        // 環境検出の強化
        const enhancedEnvironmentInfo = (() => {
          try {
            // シミュレータ/エミュレータ判定
            let isSimulator = false;
            try {
              // __DEVフラグとプラットフォームを使った簡易判定
              if (Platform.OS === "ios") {
                // iOSシミュレータの場合、特定のプロパティをチェック
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                isSimulator =
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                  (global as any).__REMOTEDEV__ === true ||
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
                  (navigator as any)?.product === "ReactNative";
              } else if (Platform.OS === "android") {
                // Androidエミュレータの簡易判定
                isSimulator = false; // 安全のためデフォルトはfalse
              }
            } catch {
              // 判定エラーは無視
            }

            // localhost問題の検出
            const urlString = error.config?.url ?? "";
            const hasLocalhost =
              urlString.includes("localhost") ||
              urlString.includes("127.0.0.1");
            const localhostWarning =
              hasLocalhost && !isSimulator
                ? "Physical device cannot access localhost. Use machine IP or ngrok."
                : undefined;

            // ポート番号の検出
            const portMatch = urlString.match(/:(\d+)/);
            const port = portMatch != null ? portMatch[1] : undefined;

            // プロトコルの検出
            const protocol = urlString.startsWith("https")
              ? "https"
              : urlString.startsWith("http")
              ? "http"
              : undefined;

            // IPv6アドレスの検出
            const hasIPv6 = /\[[\da-fA-F:]+\]/.test(urlString);

            return {
              isSimulator,
              hasLocalhost,
              localhostWarning,
              port,
              protocol,
              hasIPv6,
              urlComponents: {
                full: urlString,
                protocol,
                port,
                hasIPv6,
              },
            };
          } catch {
            return {
              error: "Failed to collect environment info",
            };
          }
        })();

        // リクエスト情報の完全記録
        const fullRequestInfo = (() => {
          try {
            // ヘッダーの安全な取得（認証情報をマスク）
            const sanitizedHeaders = (() => {
              if (error.config?.headers == null) return undefined;
              const headers = { ...error.config.headers };
              // 認証情報をマスク
              const sensitiveKeys = [
                "authorization",
                "cookie",
                "x-api-key",
                "api-key",
              ];
              Object.keys(headers).forEach((key) => {
                if (
                  sensitiveKeys.some((sensitive) =>
                    key.toLowerCase().includes(sensitive)
                  )
                ) {
                  headers[key] = "[REDACTED]";
                }
              });
              return headers;
            })();

            // リクエストボディの安全な取得（大きすぎる場合は切り詰め）
            const sanitizedData = (() => {
              if (error.config?.data == null) return undefined;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              const dataStr =
                typeof error.config.data === "string"
                  ? error.config.data
                  : JSON.stringify(error.config.data);
              // 10KBを超える場合は切り詰め
              if (dataStr != null && dataStr.length > 10240) {
                return {
                  truncated: true,
                  size: dataStr.length,
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  preview: `${dataStr.substring(0, 1024)}...[truncated]`,
                };
              }
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return error.config.data;
            })();

            return {
              method: error.config?.method,
              url: error.config?.url,
              baseURL: error.config?.baseURL,
              headers: sanitizedHeaders,
              params: error.config?.params,
              data: sanitizedData,
              timeout: error.config?.timeout,
              withCredentials: error.config?.withCredentials,
              responseType: error.config?.responseType,
              maxContentLength: error.config?.maxContentLength,
              maxBodyLength: error.config?.maxBodyLength,
              // タイミング情報
              timestamp: new Date().toISOString(),
            };
          } catch {
            return {
              error: "Failed to collect request info",
            };
          }
        })();

        networkDiagnostics = {
          // 環境検出情報（第6章準拠）
          environment: enhancedEnvironmentInfo,

          // 完全なリクエスト情報（第6章準拠）
          fullRequest: fullRequestInfo,

          // Axios標準のエラーJSON（toJSON利用）
          axiosErrorJson: error.toJSON != null ? error.toJSON() : undefined,

          // XMLHttpRequestの状態（React Native）
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          readyState: (error.request as any)?.readyState,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          status: (error.request as any)?.status,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          statusText: (error.request as any)?.statusText,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          responseURL: (error.request as any)?.responseURL,

          // エラーオブジェクトの詳細
          errorCode: error.code,
          errorMessage: error.message,
          errorStack: error.stack?.substring(0, 500), // スタックトレースの一部

          // リクエストの詳細
          requestMethod: error.config?.method,
          requestURL: error.config?.url,
          requestTimeout: error.config?.timeout,

          // Platform特有の情報
          platform: Platform.OS,
          platformVersion: Platform.Version,

          // タイミング情報
          timestamp: new Date().toISOString(),

          // ネットワーク状態の推測
          possibleCauses: [
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
            (error.request as any)?.status === 0
              ? "CORS or Network failure"
              : null,
            error.code === "ECONNABORTED" ? "Connection aborted" : null,
            error.code === "ETIMEDOUT" ? "Connection timeout" : null,
            error.request == null ? "Request creation failed" : null,
          ].filter(Boolean),
        };

        // iOS特有のエラー情報を追加
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions
        if (
          Platform.OS === "ios" &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          (error.request as any)?._response != null
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
          networkDiagnostics.iosResponse = String(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
            (error.request as any)._response
          ).substring(0, 200);
        }

        // GraphQL情報を抽出してログに含める
        const graphqlInfo =
          error.config != null ? extractGraphQLInfo(error.config.data) : {};

        // Network Error診断情報をSlackに通知
        // eslint-disable-next-line no-void
        void notify(
          {
            error: "Network Error Diagnostics",
            // GraphQL Operation名を先頭に含めて検索しやすくする
            graphqlOperation: graphqlInfo.operationName ?? "Unknown",
            graphqlVariables: graphqlInfo.variables,
            diagnostics: networkDiagnostics,
            config:
              error.config != null
                ? {
                    url: error.config.url,
                    method: error.config.method,
                    timeout: error.config.timeout,
                    operationName: graphqlInfo.operationName,
                    queryPreview: graphqlInfo.queryPreview,
                  }
                : undefined,
          },
          { level: "debug" }
        );
      } catch (diagError) {
        networkDiagnostics.diagnosticsError = String(diagError);
      }
    } else if (
      error.response?.status !== undefined &&
      error.response.status >= 500
    ) {
      errorCode = "ServiceDown";
    } else if (
      error.code === "ECONNABORTED" ||
      (typeof error.message === "string" && error.message.includes("timeout"))
    ) {
      // タイムアウトエラー
      errorCode = "NetworkError";
      networkDiagnostics.timeoutDetected = true;
    } else if (error.response === undefined && error.request === undefined) {
      // クライアントサイドエラー（ファイル処理エラーなど）
      errorCode = "Unknown";
    }

    // multipartアップロードリクエストの場合、グローバルエラー画面（「オフラインになっています」）の
    // 表示をスキップし、呼び出し元（fetchMultipartGraphQL）のフォールバック戦略に処理を委譲する。
    // 503メンテナンス、エラー診断ログ、Slack通知は上記で通常通り実行済み。
    if (error.config?._isMultipartUpload !== true) {
      await handleSetError({
        code: errorCode,
        message: result.message,
        request:
          error.config != null
            ? collectRequestInfo(error.config as InternalAxiosRequestConfig)
            : undefined,
        response: error.response,
        error,
        severity: "error",
        details: {
          statusCode: error.response?.status,
          statusText: error.response?.statusText,
          originalError: result.message,
          timestamp: new Date().toISOString(),
          // Network Error診断情報を追加
          ...(Object.keys(networkDiagnostics).length > 0 && {
            networkDiagnostics,
          }),
        },
      });
    }
    throw error;
  }
);

async function fetchGraphQL(text: string, variables: Variables) {
  const { data }: { data: AxiosResponse<ApiResponse> } =
    await axiosInstance.post(
      apiEndpoint,
      JSON.stringify({
        query: text,
        variables,
      })
    );
  return data;
}

// TODO: graphql multipart request spec に則った実装.
//       Relay の良さげな実装が出たら入れ替えたほうが良さそう.
//  ref: https://github.com/jaydenseric/graphql-multipart-request-spec
// TODO: axiosを1.3.4に上げるとFormDataがうまく送信できなくなるので調査・対応が必要
async function fetchMultipartGraphQL(
  params: RequestParameters,
  variables: Variables,
  _cacheConfig: CacheConfig,
  uploadables: UploadableMap
) {
  const formData = new FormData();
  formData.append(
    "operations",
    JSON.stringify({ query: params.text, variables })
  );
  const map: Record<string, string[]> = {};
  Object.keys(uploadables).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(uploadables, key)) {
      map[key] = [key];
    }
  });
  formData.append("map", JSON.stringify(map));

  Object.keys(uploadables).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(uploadables, key)) {
      const file = getFile(uploadables[key] as File | RNFileType);
      if (Platform.OS === "web") {
        formData.append(key, file as File);
      } else {
        // React Native環境ではファイル名を第3引数として渡す
        const rnFile = file as RNFileType;

        // ファイルアップロード詳細ログ（デバッグ用）
        try {
          // ファイルの検証とログ（防御的に実装）
          const hasUri = rnFile?.uri != null && rnFile.uri !== "";
          const hasName = rnFile?.name != null && rnFile.name !== "";
          const hasType = rnFile?.type != null && rnFile.type !== "";

          const fileValidation = {
            hasUri,
            uriFormat:
              hasUri && typeof rnFile.uri === "string"
                ? {
                    startsWithFile: rnFile.uri.startsWith("file://"),
                    startsWithHttp: rnFile.uri.startsWith("http"),
                    isAbsolutePath: rnFile.uri.startsWith("/"),
                    uriLength: rnFile.uri.length,
                    uriSample:
                      rnFile.uri.length > 50
                        ? `${rnFile.uri.substring(
                            0,
                            30
                          )}...${rnFile.uri.substring(rnFile.uri.length - 20)}`
                        : rnFile.uri,
                  }
                : null,
            hasName,
            hasType,
            fileInfo: {
              name: rnFile?.name ?? "unknown",
              type: rnFile?.type ?? "unknown",
              extension:
                hasName && typeof rnFile.name === "string"
                  ? rnFile.name.split(".").pop()?.toLowerCase()
                  : undefined,
              isVideo:
                (hasType &&
                  typeof rnFile.type === "string" &&
                  rnFile.type.startsWith("video/")) ||
                (hasName &&
                  typeof rnFile.name === "string" &&
                  /\.(mov|mp4|avi|mkv)$/i.test(rnFile.name)),
              isImage:
                (hasType &&
                  typeof rnFile.type === "string" &&
                  rnFile.type.startsWith("image/")) ||
                (hasName &&
                  typeof rnFile.name === "string" &&
                  /\.(jpg|jpeg|png|gif|webp)$/i.test(rnFile.name)),
            },
          };

          // 問題のある状態を検出
          const issues = [];
          if (!fileValidation.hasUri) issues.push("URI_MISSING");
          if (
            fileValidation.hasUri &&
            fileValidation.uriFormat?.startsWithFile === false &&
            fileValidation.uriFormat?.startsWithHttp === false &&
            fileValidation.uriFormat?.isAbsolutePath === true
          ) {
            issues.push("MISSING_FILE_PROTOCOL");
          }
          if (!fileValidation.hasName) issues.push("NAME_MISSING");
          if (!fileValidation.hasType) issues.push("TYPE_MISSING");
          if (fileValidation.fileInfo.isVideo) issues.push("VIDEO_FILE");

          // 問題がある場合、またはビデオファイルの場合は詳細ログを送信
          if (issues.length > 0 || fileValidation.fileInfo.isVideo) {
            notify(
              {
                type: "File Upload Validation",
                searchableType: "FILE_UPLOAD_VALIDATION",
                validation: {
                  component: "fetchMultipartGraphQL",
                  uploadKey: key,
                  issues,
                  fileValidation,
                  platform: Platform.OS,
                  timestamp: new Date().toISOString(),
                  version: appVersion ?? "unknown",
                },
              },
              {
                level:
                  issues.includes("URI_MISSING") ||
                  issues.includes("MISSING_FILE_PROTOCOL")
                    ? "warning"
                    : "debug",
                skipDeviceInfo: true,
              }
            ).catch(() => {
              // 通知エラーは無視
            });
          }
        } catch (e) {
          // ログ処理のエラーは無視
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
        formData.append(key, rnFile as any, rnFile.name);
      }
    }
  });

  // iOS向けのヘッダー設定最適化
  const headers: Record<string, string> = {};
  if (Platform.OS === "ios") {
    // iOSの場合、明示的にContent-Typeを設定
    headers["Content-Type"] = "multipart/form-data";
    headers.Accept = "application/json";

    // iOS特別ヘッダー設定の監視（monitorレベルで通知）
    // ファイルがある場合のみ監視通知を送信
    if (Object.keys(uploadables).length > 0) {
      try {
        notify(
          {
            type: "iOS Multipart Upload",
            searchableType: "iOS_MULTIPART_HEADERS",
            monitoring: {
              component: "fetchMultipartGraphQL",
              action: "ios_headers_applied",
              headers,
              platform: Platform.OS,
              uploadableKeys: Object.keys(uploadables),
              uploadableCount: Object.keys(uploadables).length,
              timestamp: new Date().toISOString(),
              version: appVersion,
            },
          },
          { level: "monitor", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });
      } catch (e) {
        // 監視処理のエラーは無視
      }
    }
  } else {
    // その他のプラットフォームでは既存の設定を使用
    headers["Content-Type"] = "multipart/form-data";
  }

  // リクエスト送信前の最終ログ
  try {
    // FormDataの内容を安全に取得
    const formDataInfo = {
      hasOperations: false,
      hasMap: false,
      fileCount: 0,
      files: [] as Array<{ key: string; type?: string; name?: string }>,
    };

    // FormDataの内容を検査（React Native環境）
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formDataWithParts = formData as any;
      if (
        Platform.OS !== "web" &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        formDataWithParts._parts != null &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        Array.isArray(formDataWithParts._parts)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        const parts = formDataWithParts._parts as Array<[string, any]>;
        parts.forEach(([partKey, partValue]) => {
          try {
            if (typeof partKey === "string") {
              if (partKey === "operations") formDataInfo.hasOperations = true;
              if (partKey === "map") formDataInfo.hasMap = true;
              if (partKey.startsWith("variables.input.files")) {
                formDataInfo.fileCount += 1;
                if (typeof partValue === "object" && partValue != null) {
                  formDataInfo.files.push({
                    key: partKey,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    type:
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      typeof partValue.type === "string"
                        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                          partValue.type
                        : undefined,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    name:
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      typeof partValue.name === "string"
                        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                          partValue.name
                        : undefined,
                  });
                }
              }
            }
          } catch {
            // 個々のパート処理のエラーは無視
          }
        });
      }
    } catch {
      // FormData解析のエラーは無視（ログ機能なので本処理に影響させない）
    }

    // ファイルアップロードがある場合のみログ
    if (formDataInfo.fileCount > 0) {
      notify(
        {
          type: "File Upload Request",
          searchableType: "FILE_UPLOAD_REQUEST",
          request: {
            component: "fetchMultipartGraphQL",
            endpoint: apiEndpoint,
            formDataInfo,
            headers,
            timeout: 1800000,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "debug", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });
    }
  } catch (e) {
    // ログ処理のエラーは無視
  }

  // Web環境でのFormData送信最適化
  // 既存のFormDataオブジェクトを送信する場合は、post()を使用
  // postForm()はプレーンオブジェクトを内部でFormDataに変換するため、
  // 既存のFormDataを渡すと構造が破壊される
  if (Platform.OS === "web" && Object.keys(uploadables).length > 0) {
    // リクエストIDを生成（フロントエンドとRailsのログを紐付けるため）
    const requestId = Crypto.randomUUID();

    // FormData内容の完全検証（Content-Type: falseバグの原因特定用）
    try {
      const formDataValidation = {
        hasOperations: false,
        operationsContent: null as unknown,
        hasQuery: false,
        queryLength: 0,
        hasVariables: false,
        hasMap: false,
        mapContent: null as unknown,
        fileKeys: [] as string[],
        totalParts: 0,
        validationErrors: [] as string[],
      };

      // FormDataの内容を検証（Web APIを使用）
      // Web環境のFormDataはentriesメソッドを持つが、React Nativeの型定義には含まれていない
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const formDataEntries = Array.from((formData as any).entries()) as Array<
        [string, unknown]
      >;
      formDataValidation.totalParts = formDataEntries.length;

      formDataEntries.forEach(([key, value]) => {
        if (key === "operations") {
          formDataValidation.hasOperations = true;
          if (typeof value === "string") {
            try {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const parsed = JSON.parse(value);
              formDataValidation.operationsContent = parsed;
              formDataValidation.hasQuery =
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                typeof parsed.query === "string" && parsed.query.length > 0;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              formDataValidation.queryLength = parsed.query?.length ?? 0;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              formDataValidation.hasVariables = parsed.variables != null;

              if (!formDataValidation.hasQuery) {
                formDataValidation.validationErrors.push(
                  "MISSING_QUERY_IN_OPERATIONS"
                );
              }
            } catch {
              formDataValidation.validationErrors.push(
                "INVALID_JSON_IN_OPERATIONS"
              );
            }
          }
        } else if (key === "map") {
          formDataValidation.hasMap = true;
          if (typeof value === "string") {
            try {
              formDataValidation.mapContent = JSON.parse(value);
            } catch {
              formDataValidation.validationErrors.push("INVALID_JSON_IN_MAP");
            }
          }
        } else {
          formDataValidation.fileKeys.push(key);
        }
      });

      // 検証結果のログ
      // eslint-disable-next-line no-void
      void notify(
        {
          type: "Web FormData Validation",
          searchableType: "WEB_FORMDATA_VALIDATION",
          requestId,
          validation: {
            ...formDataValidation,
            uploadablesCount: Object.keys(uploadables).length,
            isValid:
              formDataValidation.validationErrors.length === 0 &&
              formDataValidation.hasOperations &&
              formDataValidation.hasQuery &&
              formDataValidation.hasMap,
            timestamp: new Date().toISOString(),
          },
        },
        {
          level:
            formDataValidation.validationErrors.length > 0 ? "error" : "debug",
          skipDeviceInfo: true,
        }
      ).catch(() => {
        // 通知エラーは無視
      });

      // 検証エラーがある場合は警告
      if (formDataValidation.validationErrors.length > 0) {
        // eslint-disable-next-line no-void
        void notify(
          {
            type: "FormData Validation Failed",
            searchableType: "FORMDATA_VALIDATION_ERROR",
            requestId,
            errors: formDataValidation.validationErrors,
            details: formDataValidation,
            timestamp: new Date().toISOString(),
          },
          { level: "error", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });
      }
    } catch (validationError) {
      // eslint-disable-next-line no-void
      void notify(
        {
          type: "FormData Validation Error",
          searchableType: "FORMDATA_VALIDATION_EXCEPTION",
          requestId,
          error: {
            message:
              validationError instanceof Error
                ? validationError.message
                : String(validationError),
            timestamp: new Date().toISOString(),
          },
        },
        { level: "error", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });
    }

    try {
      // 通常のpostメソッドを使用し、transformRequestでContent-Typeヘッダーを削除
      // ブラウザが自動的にmultipart/form-data; boundary=...を設定する
      const { data }: { data: AxiosResponse<ApiResponse> } =
        await axiosInstance.post(apiEndpoint, formData, {
          _isMultipartUpload: true,
          headers: {
            "X-Request-ID": requestId,
          },
          timeout: 1800000,
          transformRequest: [
            (requestData, requestHeaders) => {
              const trackingId = `transform_${Date.now()}_${Math.random()
                .toString(36)
                .substring(2, 8)}`;

              try {
                // Phase 1: transformRequest実行開始ログ
                const beforeContentType = requestHeaders?.["Content-Type"];
                const isFormData = requestData instanceof FormData;

                // eslint-disable-next-line no-void
                void notify(
                  {
                    type: "TransformRequest Execution",
                    searchableType: "TRANSFORM_REQUEST_START",
                    requestId,
                    trackingId,
                    phase: "before_transformation",
                    state: {
                      isFormData,
                      beforeContentType,
                      hasHeaders: requestHeaders != null,
                      headersKeys:
                        requestHeaders != null
                          ? Object.keys(requestHeaders)
                          : [],
                      formDataConstructor:
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        requestData?.constructor?.name ?? "unknown",
                      timestamp: new Date().toISOString(),
                    },
                  },
                  { level: "debug", skipDeviceInfo: true }
                ).catch(() => {
                  // 通知エラーは無視
                });

                // Phase 2: Content-Type削除実行
                if (isFormData && requestHeaders != null) {
                  const hadContentType = "Content-Type" in requestHeaders;
                  // eslint-disable-next-line no-param-reassign
                  delete requestHeaders["Content-Type"];
                  const afterContentType = requestHeaders?.["Content-Type"];

                  // eslint-disable-next-line no-void
                  void notify(
                    {
                      type: "TransformRequest Content-Type Deletion",
                      searchableType: "TRANSFORM_REQUEST_DELETE_CONTENT_TYPE",
                      requestId,
                      trackingId,
                      phase: "deletion",
                      operation: {
                        hadContentType,
                        beforeValue: beforeContentType,
                        afterValue: afterContentType,
                        deletionSuccessful: afterContentType === undefined,
                        timestamp: new Date().toISOString(),
                      },
                    },
                    { level: "debug", skipDeviceInfo: true }
                  ).catch(() => {
                    // 通知エラーは無視
                  });
                }

                // Phase 3: transformRequest完了ログ
                // eslint-disable-next-line no-void
                void notify(
                  {
                    type: "TransformRequest Completed",
                    searchableType: "TRANSFORM_REQUEST_COMPLETE",
                    requestId,
                    trackingId,
                    phase: "after_transformation",
                    result: {
                      isFormData,
                      afterContentType: requestHeaders?.["Content-Type"],
                      returnedDataType:
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        requestData?.constructor?.name ?? "unknown",
                      timestamp: new Date().toISOString(),
                    },
                  },
                  { level: "debug", skipDeviceInfo: true }
                ).catch(() => {
                  // 通知エラーは無視
                });

                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return requestData;
              } catch (error) {
                // Phase 4: transformRequest エラーログ
                // eslint-disable-next-line no-void
                void notify(
                  {
                    type: "TransformRequest Error",
                    searchableType: "TRANSFORM_REQUEST_ERROR",
                    requestId,
                    trackingId,
                    error: {
                      message:
                        error instanceof Error ? error.message : String(error),
                      stack: error instanceof Error ? error.stack : undefined,
                      timestamp: new Date().toISOString(),
                    },
                  },
                  { level: "error", skipDeviceInfo: true }
                ).catch(() => {
                  // 通知エラーは無視
                });

                throw error;
              }
            },
          ],
        });

      // 成功ログ
      notify(
        {
          type: "Web FormData Upload Success",
          searchableType: "WEB_FORMDATA_UPLOAD_SUCCESS",
          requestId,
          details: {
            method: "post_with_transformRequest",
            uploadablesCount: Object.keys(uploadables).length,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "info", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      return data;
    } catch (error) {
      // Web環境でのアップロードエラーログ
      if (error instanceof Error) {
        notify(
          {
            type: "Web FormData Upload Failed - Trying Fallback",
            searchableType: "WEB_FORMDATA_UPLOAD_ERROR_FALLBACK",
            requestId,
            error: {
              message: error.message,
              name: error.name,
              uploadablesCount: Object.keys(uploadables).length,
              platform: Platform.OS,
              timestamp: new Date().toISOString(),
              version: appVersion,
            },
          },
          { level: "warning", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });
      }

      // Web版フォールバック戦略を実行
      // FormDataストリームは消費済みのため、元のFile参照から再構築する（axios#6454）
      const fileEntries: Record<string, File> = {};
      Object.keys(uploadables).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(uploadables, key)) {
          fileEntries[key] = uploadables[key] as File;
        }
      });

      const token = await SecureStoreManager.getAccessToken();
      return tryWebFormDataUploadFallback({
        apiEndpoint,
        operationsJson: JSON.stringify({ query: params.text, variables }),
        mapJson: JSON.stringify(map),
        files: fileEntries,
        headers: {
          ...(token != null ? { Authorization: `Bearer ${token}` } : {}),
          AppVersion: appVersion ?? "unknown",
        },
        axiosInstance,
        requestId,
      });
    }
  }

  // iOS環境でのFormData送信問題に対するワークアラウンド実装
  // transformRequestを使用してFormDataの変換を防ぐ（GitHub issueで推奨される方法）
  // 参照: https://github.com/axios/axios/issues/1321, #1567, #4406
  if (Platform.OS === "ios" && Object.keys(uploadables).length > 0) {
    try {
      // iOS用ログ: transformRequestワークアラウンドを試行
      notify(
        {
          type: "iOS FormData Workaround",
          searchableType: "IOS_FORMDATA_WORKAROUND_ATTEMPT",
          details: {
            method: "transformRequest",
            uploadablesCount: Object.keys(uploadables).length,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "debug", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      const { data }: { data: AxiosResponse<ApiResponse> } =
        await axiosInstance.post(apiEndpoint, formData, {
          _isMultipartUpload: true,
          timeout: 1800000,
          headers,
          // FormDataの変換を防ぐtransformRequestワークアラウンド
          transformRequest: [() => formData],
        });

      // 成功ログ
      notify(
        {
          type: "iOS FormData Workaround Success",
          searchableType: "IOS_FORMDATA_WORKAROUND_SUCCESS",
          details: {
            method: "transformRequest",
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "info", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      return data;
    } catch (transformRequestError) {
      // transformRequestが失敗した場合、既存の方法にfallback
      notify(
        {
          type: "iOS FormData Workaround Failed - Using Fallback",
          searchableType: "IOS_FORMDATA_WORKAROUND_FALLBACK",
          details: {
            method: "transformRequest_failed",
            errorMessage:
              transformRequestError instanceof Error
                ? transformRequestError.message
                : "Unknown error",
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
        },
        { level: "warning", skipDeviceInfo: true }
      ).catch(() => {
        // 通知エラーは無視
      });

      // 既存の方法でリトライ
      // eslint-disable-next-line no-useless-catch
      try {
        const { data }: { data: AxiosResponse<ApiResponse> } =
          await axiosInstance.post(apiEndpoint, formData, {
            _isMultipartUpload: true,
            timeout: 1800000,
            headers,
          });

        // fallback成功ログ
        notify(
          {
            type: "iOS FormData Fallback Success",
            searchableType: "IOS_FORMDATA_FALLBACK_SUCCESS",
            details: {
              method: "standard",
              platform: Platform.OS,
              timestamp: new Date().toISOString(),
              version: appVersion,
            },
          },
          { level: "info", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });

        return data;
      } catch (fallbackError) {
        // 既存の方法も失敗した場合、追加のフォールバック戦略を試行
        notify(
          {
            type: "iOS FormData Standard Fallback Failed - Using Extended Strategies",
            searchableType: "IOS_FORMDATA_EXTENDED_FALLBACK",
            details: {
              errorMessage:
                fallbackError instanceof Error
                  ? fallbackError.message
                  : "Unknown error",
              platform: Platform.OS,
              timestamp: new Date().toISOString(),
              version: appVersion,
            },
          },
          { level: "warning", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });

        // 追加のフォールバック戦略を実行
        // すべてのフォールバック戦略が失敗した場合、例外が外側に伝播する
        return tryIOSFormDataUploadFallback({
          uploadables,
          apiEndpoint,
          formData,
          headers,
          axiosInstance,
          appVersion,
        });
      }
    }
  }

  // iOS以外のプラットフォーム、またはアップロード対象がない場合は既存の方法を使用
  try {
    const { data }: { data: AxiosResponse<ApiResponse> } =
      await axiosInstance.post(apiEndpoint, formData, {
        _isMultipartUpload: true,
        timeout: 1800000,
        headers,
      });
    return data;
  } catch (error) {
    // アップロードエラーの詳細ログ
    if (error instanceof Error) {
      try {
        const errorInfo = {
          message: error.message,
          name: error.name,
          isNetworkError: error.message === "Network Error",
          isAxiosError: isAxiosError(error),
          response: isAxiosError(error)
            ? {
                status: error.response?.status,
                statusText: error.response?.statusText,
                headers: error.response?.headers,
              }
            : null,
          request:
            isAxiosError(error) && error.config != null
              ? {
                  url: error.config.url,
                  method: error.config.method,
                  timeout: error.config.timeout,
                }
              : null,
        };

        notify(
          {
            type: "Multipart Upload Failed",
            searchableType: "MULTIPART_UPLOAD_ERROR",
            error: errorInfo,
            platform: Platform.OS,
            timestamp: new Date().toISOString(),
            version: appVersion,
          },
          { level: "error", skipDeviceInfo: false }
        ).catch(() => {
          // 通知エラーは無視
        });
      } catch (e) {
        // ログ処理のエラーは無視
      }
    }
    throw error;
  }
}

// React Native環境ではFileオブジェクトを直接FormDataに追加できないため、
// 特殊なオブジェクト形式に変換する必要がある
// https://github.com/facebook/react-native/issues/22681
type RNFileType = { uri: string; name: string; type: string };

function getFile(data: File | RNFileType): File | RNFileType {
  if (Platform.OS === "web") {
    return data as File;
  }

  try {
    // React Native環境では、ImagePickerから渡される軽量オブジェクトを使用
    // Fileオブジェクトのような形式だが、実際はuri, name, typeを持つ軽量オブジェクト
    const rnFile = data as RNFileType;

    // URIが存在することを確認（安全性チェックだが、既存動作を妨げないように）
    if (rnFile.uri === undefined || rnFile.uri === "") {
      // eslint-disable-next-line no-console
      console.warn(
        "Missing URI in file object, falling back to original data:",
        rnFile
      );

      // バグ調査用Slack通知（安全に実行）
      try {
        const bugTrackingId = `iOS_IMAGE_UPLOAD_BUG_RelayEnvironment_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 8)}`;
        notify(
          {
            type: "RelayEnvironmentError: MissingFileURI",
            searchableType: "iOS_IMAGE_UPLOAD_ERROR_MissingFileURI",
            bugTracking: {
              id: bugTrackingId,
              component: "RelayEnvironment",
              issue: "iOS_IMAGE_UPLOAD_FAILURE",
              keywords: [
                "iOS",
                "画像アップロード",
                "URI欠損",
                "FormData",
                "React Native",
                "RelayEnvironment",
              ],
              version: appVersion,
              timestamp: new Date().toISOString(),
            },
            error: {
              message: "File object missing URI",
              context: "getFile function in RelayEnvironment",
            },
            fileData: {
              hasName: rnFile.name !== undefined,
              hasType: rnFile.type !== undefined,
              hasUri: false,
            },
          },
          { level: "warning", skipDeviceInfo: true }
        );
        // eslint-disable-next-line no-console
        console.log(
          `[${bugTrackingId}] RelayEnvironment bug tracking notification sent`
        );
      } catch (notifyError) {
        // eslint-disable-next-line no-console
        console.warn(
          "RelayEnvironment Slack notification failed:",
          notifyError
        );
      }

      // 既存動作を保持するため、元のdataをそのまま返す
      return data;
    }

    // iOS向けのfile://プレフィックス削除処理
    let processedUri = rnFile.uri;
    if (Platform.OS === "ios" && processedUri.startsWith("file://")) {
      processedUri = processedUri.replace("file://", "");

      // iOS file://プレフィックス削除の監視（monitorレベルで通知）
      try {
        notify(
          {
            type: "iOS File Processing",
            searchableType: "iOS_FILE_PREFIX_REMOVAL",
            monitoring: {
              component: "getFile",
              action: "file_prefix_removed",
              originalUri: rnFile.uri,
              processedUri,
              fileName: rnFile.name,
              fileType: rnFile.type,
              platform: Platform.OS,
              timestamp: new Date().toISOString(),
              version: appVersion,
            },
          },
          { level: "monitor", skipDeviceInfo: true }
        ).catch(() => {
          // 通知エラーは無視
        });
      } catch (e) {
        // 監視処理のエラーは無視
      }
    }

    return {
      uri: processedUri, // 実際のファイルURI（iOSの場合はfile://を削除）
      name:
        rnFile.name !== undefined && rnFile.name !== ""
          ? rnFile.name
          : `file_${Date.now()}`, // ファイル名
      type:
        rnFile.type !== undefined && rnFile.type !== ""
          ? rnFile.type
          : "application/octet-stream", // MIMEタイプ
    };
  } catch (error) {
    // 新しい処理が失敗した場合は既存動作にフォールバック
    // eslint-disable-next-line no-console
    console.warn("getFile enhancement failed, using original behavior:", error);

    // バグ調査用Slack通知（安全に実行）
    try {
      const bugTrackingId = `iOS_IMAGE_UPLOAD_BUG_RelayEnvironment_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 8)}`;
      notify(
        {
          type: "RelayEnvironmentError: GetFileFailure",
          searchableType: "iOS_IMAGE_UPLOAD_ERROR_GetFileFailure",
          bugTracking: {
            id: bugTrackingId,
            component: "RelayEnvironment",
            issue: "iOS_IMAGE_UPLOAD_FAILURE",
            keywords: [
              "iOS",
              "画像アップロード",
              "getFile関数エラー",
              "FormData",
              "React Native",
              "RelayEnvironment",
            ],
            version: appVersion,
            timestamp: new Date().toISOString(),
          },
          error: {
            message: resolveError(error).message,
            stack: error instanceof Error ? error.stack : undefined,
          },
        },
        { level: "warning", skipDeviceInfo: true }
      );
      // eslint-disable-next-line no-console
      console.log(
        `[${bugTrackingId}] RelayEnvironment getFile failure notification sent`
      );
    } catch (notifyError) {
      // eslint-disable-next-line no-console
      console.warn(
        "RelayEnvironment getFile failure notification failed:",
        notifyError
      );
    }

    return data;
  }
}

async function retryWithtokenRefresh(response: AxiosResponse<ApiResponse>) {
  const tokenSet = await refreshToken(relayEnvironment);
  const newConfig = { ...response.config };

  if (newConfig.headers != null) {
    newConfig.headers.Authorization = `Bearer ${tokenSet.refreshToken.token}`;
  }
  const result = await axiosInstance.request(newConfig);
  return result;
}

// Relay passes a "params" object with the query name and text. So we define a helper function
// to call our fetchGraphQL utility with params.text.
async function fetchRelay(
  params: RequestParameters,
  variables: Variables,
  _cacheConfig: CacheConfig,
  uploadables?: UploadableMap | null
) {
  if (uploadables != null) {
    return fetchMultipartGraphQL(params, variables, _cacheConfig, uploadables);
  }
  return fetchGraphQL(params.text as string, variables);
}

async function handleError(
  error: ResponseError | undefined,
  config?: InternalAxiosRequestConfig,
  response?: AxiosResponse
) {
  try {
    const errorCode = error?.extensions?.code ?? "Unknown";
    const errorMessage = error?.message ?? "Unknown error occurred";

    const detailedError: DetailedErrorType = {
      code: errorCode as ErrorCodeType,
      message: errorMessage,
      request: config != null ? collectRequestInfo(config) : undefined,
      response,
      error,
    };

    switch (errorCode) {
      case "UnsupportedVersion":
        await handleSetError({
          ...detailedError,
          code: "Upgrade",
          message: "アプリケーションのアップデートが必要です",
          severity: "warning",
        });
        break;
      case "Unauthorized":
      case "InvalidToken":
        await handleSetError({
          ...detailedError,
          code: "Expired",
          message: "認証情報が無効です",
          severity: "warning",
        });
        break;
      case "Unknown":
        await handleSetError({
          ...detailedError,
          severity: "error",
        });
        break;
      default:
        throw new Error(JSON.stringify(error));
    }
  } catch (e: unknown) {
    const result = resolveError(e);
    await handleSetError({
      code: "ServiceDown",
      message: result.message,
      error,
      severity: "error",
      details: {
        originalError: result.message,
        timestamp: new Date().toISOString(),
      },
    });
  }
}

// Export a singleton instance of Relay Environment configured with our network function:
const relayEnvironment = new Environment({
  network: Network.create(fetchRelay),
  store: new Store(new RecordSource()),
});

export default relayEnvironment;
